/**
 * Added to Community Notification Component
 * Displays a notification when a user is added to a community by another user
 * Shows adder info and community preview (no action buttons since user is already a member)
 */

import { EllipsisVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { CommunityNotificationCard } from "./CommunityNotificationCard"

interface AddedToCommunityNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface AddedToCommunityNotificationData {
  adderId?: string
  adderName?: string
  adderUsername?: string
  communityId?: string
  communityName?: string
  communityHandle?: string
  communityCoverImageUrl?: string
  city?: string
  state?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function AddedToCommunityNotification({
  notification,
  onRead,
  onUnread,
}: AddedToCommunityNotificationProps) {
  const navigate = useNavigate()

  if (!notification.triggeredByUser || !notification.data) {
    return null
  }

  const user = notification.triggeredByUser
  const data = notification.data as AddedToCommunityNotificationData

  const adderUsername = data.adderUsername
  const communityCoverImageUrl = data.communityCoverImageUrl
  const communityId = data.communityId
  const communityName = data.communityName || "a community"
  const city = data.city
  const state = data.state
  const location = city && state ? `${city}, ${state}` : null
  const isRead = notification.isRead

  const displayImageUrl = getUserDisplayImage(user)
  const displayName = user.name || "Someone"

  const relativeTime = formatNotificationTime(notification.createdAt)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/profile/${user.id}`)
    if (!isRead) {
      onRead?.()
    }
  }

  const handleCommunityClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/communities/${communityId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* User avatar */}
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleAvatarClick}
          >
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {adderUsername && <span className="text-stone text-[14px]">@{adderUsername}</span>}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              <span className="font-semibold">{displayName}</span> added you to{" "}
              <span className="font-medium cursor-pointer" onClick={handleCommunityClick}>
                {communityName}
              </span>
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <CommunityNotificationCard
        coverImageUrl={communityCoverImageUrl}
        title={communityName}
        location={location}
        onClick={handleCommunityClick}
      />
    </div>
  )
}
